import { Analytics } from 'aws-amplify';
import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

import NavBar from '../Home/NavigationBar/NavBar';
import QuestionnaireUploadModal from './QuestionnaireUploadModal/QuestionnaireUploadModal';
import ValidationErrorsModal from './QuestionnaireUploadModal/ValidationErrorsModal/ValidationErrorsModal';
import './styles.scss';

const sampleSurveyFileKey = "sampleSurvey";
const questionnaireFileKey = "surveyQuestionnaire";
const surveyMethodology = "surveyMethodology";
const earlySurveyResultBenchmarkKey = "earlySurveyBenchmarkingResult";
const earlySurveyResultPolicyKey = "earlySurveyPolicyResult";

const unrestrictedSurveyDataAPI = process.env.REACT_APP_UNRESTRICTED_SURVEY_DATA_API;
const surveyDataConsoleLink = "https://compensation.fwcook.com/survey-data/console";

const finalResultReleaseDate = new Date(2026, 8, 25);
const earlyResultReleaseDate = new Date(2026, 5, 26);
const today = new Date();
const surveyYear = 2026;

export default function SurveyParticipantConsole() {
    const [isLoading, setIsLoading] = useState(true);
    const [participantData, setParticipantData] = useState(null);

    const [loadError, setLoadError] = useState(false);

    const [quesitionnaireFileLink, setQuesitionnaireFileLink] = useState(null);
    const questionnaireDonwnloadLinkRef = useRef(null);

    const [sampleSurveyFileLink, setSampleSurveyFileLink] = useState(null);

    const { participantId } = useParams();

    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showErrorValidationGrid, setShowErrorValidationGrid] = useState(false);
    const [validationErrorData, setValidationErrorData] = useState([]);

    const [isDownloading, setIsDownloading] = useState(false);
    const [acceptedSubmission, setAcceptedSubmission] = useState(false);
    const [finalResultReleased, setFinalResultReleased] = useState(today > finalResultReleaseDate);
    const [earlyResultReleased, setEarlyResultReleased] = useState(today > earlyResultReleaseDate);
    const [earlyPolicyResultReleased, setEarlyPolicyResultReleased] = useState(today > earlyResultReleaseDate);

    const [surveyMethodologyFileLink, setSurveyMethodologyFileLink] = useState(null);
    const surveyMethodologyDownloadLinkRef = useRef(null);

    const [earlySurveyBenchmarkResultFileLink, setEarlySurveyBenchmarkResultFileLink] = useState(null);
    const earlySurveyBenchmarkResultDownloadLinkRef = useRef(null);

    const [earlySurveyPolicyResultFileLink, setEarlySurveyPolicyResultFileLink] = useState(null);
    const earlySurveyPolicyResultDownloadLinkRef = useRef(null);

    const launchSurveyDataConsoleLinkRef = useRef(null);

    const fetchParticipantData = () => {
        axios.get(`${unrestrictedSurveyDataAPI}/unrestricted/survey/participants?id=${participantId}`)
            .then(response => {
                setParticipantData(response.data);
                navigatedToParticipantConsoleAnalyticsEvent(response.data);
                fetchStaticFileLink(sampleSurveyFileKey, setSampleSurveyFileLink);
                fetchStaticFileLink(questionnaireFileKey, setQuesitionnaireFileLink);
                setLoadError(false); // Reset error state on successful load
            })
            .catch(error => {
                console.log(error);
                setLoadError(true); // Set error state when loading fails

                // Track the error in Analytics
                Analytics.record({
                    name: 'ParticipantDataLoadError',
                    attributes: {
                        participantId: participantId,
                        errorMessage: error.message || 'Unknown error'
                    }
                });
            })
            .finally(() => {
                setIsLoading(false); // Always set loading to false when done
            });
    }

    const fetchSubmissionData = () => {
        axios.get(`${unrestrictedSurveyDataAPI}/unrestricted/survey/submission?id=${participantId}&surveyYear=${surveyYear}`)
            .then(response => {
                if (response?.data) {
                    handleCheckSubmissionData(response.data);
                    setLoadError(false);
                }
            }).catch(error => {
                console.log(error);
                setAcceptedSubmission(false);
                Analytics.record({
                    name: 'ParticipantDataAccessError',
                    attributes: {
                        participantId: participantId,
                        errorMessage: error.message || 'Unknown error'
                    }
                });
            })
    }

    const handleCheckSubmissionData = (data) => {
        if (data.processingStatus === "ACCEPTED") {
            setAcceptedSubmission(true);
            if (earlyResultReleased) {
                fetchStaticFileLink(surveyMethodology, setSurveyMethodologyFileLink)
                fetchStaticFileLink(earlySurveyResultBenchmarkKey, setEarlySurveyBenchmarkResultFileLink);
            }
            if (earlyPolicyResultReleased) {
                fetchStaticFileLink(earlySurveyResultPolicyKey, setEarlySurveyPolicyResultFileLink);
            }

        } else {
            setAcceptedSubmission(false);
        }
    }


    const handleDownloadingMethodologyDoc = () => {
        if (surveyMethodologyDownloadLinkRef.current && surveyMethodologyFileLink) {
            surveyMethodologyDownloadLinkRef.current.href = surveyMethodologyFileLink.url;
            surveyMethodologyDownloadLinkRef.current.click();
        }
    }

    const handleDownloadingEarlySurveyBenchmarkResult = () => {
        if (earlySurveyBenchmarkResultDownloadLinkRef.current && earlySurveyBenchmarkResultFileLink) {
            earlySurveyBenchmarkResultDownloadLinkRef.current.href = earlySurveyBenchmarkResultFileLink.url;
            earlySurveyBenchmarkResultDownloadLinkRef.current.click();
            earlySurveyResultEvent();
        }
    }

    const handleDownloadingEarlySurveyPolicyResult = () => {
        if (earlySurveyPolicyResultDownloadLinkRef.current && earlySurveyPolicyResultFileLink) {
            earlySurveyPolicyResultDownloadLinkRef.current.href = earlySurveyPolicyResultFileLink.url;
            earlySurveyPolicyResultDownloadLinkRef.current.click();
            earlySurveyResultEvent();
        }
    }

    const earlySurveyResultEvent = () => {
        axios.post(`${unrestrictedSurveyDataAPI}/track-ecs-portal-events/early-result-downloaded`, {
            participantId: participantId,
            participantName: participantData.participantName,
            hubspotCompanyId: participantData.hubspotCompanyId,
            hubspotDealId: participantData.hubspotDealId
        })
            .then(response => {
                console.log(response);
            })
            .catch(error => {
                console.log(error);
            });
    }

    const logFailedQuestionnaireDownload = () => {
        Analytics.record({
            name: 'FailedQuestionnaireDownload',
            attributes: {
                participantId: participantId,
                participantName: participantData?.participantName || 'Unknown'
            }
        });
    }

    const fetchStaticFileLink = (fileType, setLink) => {
        axios.get(`${unrestrictedSurveyDataAPI}/unrestricted/survey/files?docType=${fileType}&participantId=${participantId}&surveyYear=${surveyYear}`)
            .then(response => {
                setLink(response.data);
            })
            .catch(error => {
                logFailedQuestionnaireDownload();
                console.log(error);
            });
    }

    const questionnaireDownloadedEvent = () => {
        axios.post(`${unrestrictedSurveyDataAPI}/track-ecs-portal-events/questionnaire-downloaded`, {
            participantId: participantId,
            participantName: participantData.participantName,
            hubspotCompanyId: participantData.hubspotCompanyId,
            hubspotDealId: participantData.hubspotDealId
        })
            .then(response => {
                console.log(response);
            })
            .catch(error => {
                console.log(error);
            });
    }

    const sampleSurveyDownloadedEvent = () => {
        axios.post(`${unrestrictedSurveyDataAPI}/track-ecs-portal-events/sample-questionnaire-downloaded`, {
            participantId: participantId,
            participantName: participantData.participantName,
            hubspotCompanyId: participantData.hubspotCompanyId,
            hubspotDealId: participantData.hubspotDealId
        })
            .then(response => {
                console.log(response);
            })
            .catch(error => {
                console.log(error);
            });
    }

    const navigatedToParticipantConsoleAnalyticsEvent = (participant) => {
        Analytics.record({
            name: 'NavigatedToParticipantConsole',
            attributes: {
                participantId: participant.participantId,
                participantName: participant.participantName
            }
        });
    }

    const emailAccountExecutiveAnalyticsEvent = () => {
        Analytics.record({
            name: 'EmailAccountExecutive',
            attributes: {
                participantId: participantId,
                participantName: participantData.participantName,
                accountExecutiveEmail: participantData.accountExecutiveEmail
            }
        });
    }

    const emailFwcSurveyAnalyticsEvent = () => {
        Analytics.record({
            name: 'EmailFwcSurvey',
            attributes: {
                participantId: participantId,
                participantName: participantData.participantName
            }
        });
    }

    const handleDownloadingQuestionnaire = () => {
        setIsDownloading(true);
        if (questionnaireDonwnloadLinkRef.current && quesitionnaireFileLink) {
            questionnaireDonwnloadLinkRef.current.href = quesitionnaireFileLink.url;
            questionnaireDonwnloadLinkRef.current.click();
            questionnaireDownloadedEvent();
            setIsDownloading(false);
        }
    }

    const handleOpenSurveyDataConsole = () => {
        if (launchSurveyDataConsoleLinkRef.current) {
            launchSurveyDataConsoleLinkRef.current.href = surveyDataConsoleLink;
            launchSurveyDataConsoleLinkRef.current.click();
        }
    }

    const handleCloseValidationErrorModal = () => {
        setShowErrorValidationGrid(false);
        setShowUploadModal(true);
    }

    const handleOpenValidationErrorModal = () => {
        setShowUploadModal(false);
        setShowErrorValidationGrid(true);
    }

    const handleSetValidationErrorData = (data) => {
        setValidationErrorData(data);
        setShowUploadModal(false);
        setShowErrorValidationGrid(true);
    }

    const handleRetry = () => {
        fetchParticipantData();
        fetchSubmissionData();
    }

    useEffect(() => {
        let isMounted = true;
        if (isMounted) {
            setIsLoading(true);
            setLoadError(false); // Reset error state on component mount or participantId change
            fetchParticipantData();
            fetchSubmissionData();
            fetchStaticFileLink(questionnaireFileKey, setQuesitionnaireFileLink);
            fetchStaticFileLink(sampleSurveyFileKey, setSampleSurveyFileLink);
            setFinalResultReleased(today > finalResultReleaseDate);
            setEarlyResultReleased(today > earlyResultReleaseDate);
            setEarlyPolicyResultReleased(today > earlyResultReleaseDate)
        }
        return () => { isMounted = false };
        // eslint-disable-next-line
    }, [participantId]);

    const handleContactSupport = () => {
        window.location.href = "mailto:FWCSurvey@fwcook.com?subject=Survey Portal Access Issue";

        Analytics.record({
            name: 'ErrorContactSupportClicked',
            attributes: {
                participantId: participantId
            }
        });
    };

    const safeGet = (obj, path, fallback = '') => {
        try {
            return path.split('.').reduce((o, key) => o?.[key], obj) || fallback;
        } catch (e) {
            return fallback;
        }
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="loading-container">
                    <Spinner animation="border" variant="primary" aria-label="Loading participant data" />
                </div>
            );
        } else if (loadError) {
            return (
                <div className="ecs-main-container">
                    <div className="survey-participant-console-container error-container">
                        <Alert variant="danger">
                            <Alert.Heading>Unable to load participant data</Alert.Heading>
                            <p>
                                We encountered an issue while trying to load your participant information.
                                This could be due to network connectivity issues or the participant ID may be invalid.
                            </p>
                            <hr />
                            <div className="error-actions">
                                <p>
                                    Please try refreshing the page. If the issue persists, contact us at{' '}
                                    <button
                                        className="email-link-button"
                                        onClick={handleContactSupport}
                                    >
                                        FWCSurvey@fwcook.com
                                    </button>
                                </p>
                                <button
                                    className="btn btn-primary retry-button"
                                    onClick={handleRetry}
                                >
                                    Retry
                                </button>
                            </div>
                        </Alert>
                    </div>
                </div>
            );
        } else {
            return (
                <div className='ecs-main-container'>
                    <QuestionnaireUploadModal
                        show={showUploadModal}
                        onClose={() => setShowUploadModal(false)}
                        participantMetadataRecord={participantData}
                        handleSetValidationErrorData={handleSetValidationErrorData}
                        handleOpenValidationErrorModal={handleOpenValidationErrorModal}
                    />
                    <ValidationErrorsModal data={validationErrorData} show={showErrorValidationGrid} handleClose={handleCloseValidationErrorModal} />
                    <div className='survey-participant-console-container'>
                        <h3 className='title-text'>
                            <span className='hello-text'>Hello</span>, <span className='participant-name-text'>{safeGet(participantData, 'participantName', 'User')}</span>
                        </h3>
                        <div className='welcome-text'>
                            Welcome to the 2026 FW Cook Executive Compensation Survey Portal!
                        </div>
                        <div className='survey-participant-console-content'>
                            {finalResultReleased && acceptedSubmission ? <><div className='section'>
                                <a href="#self" ref={launchSurveyDataConsoleLinkRef} target="_blank" className='link' rel="noopener noreferrer" hidden={true}>hidden</a>
                                <h4 className='section-heading'>Survey Access</h4>
                                <p>2026 Final Survey is released. We have a new portal to download the results. You can access it by clicking the button below.</p>
                                <div className='tooltip-container'>
                                    <button className='btn btn-primary download' onClick={handleOpenSurveyDataConsole}>Access Survey Data</button>
                                    <span className='tooltip-text'>Access Survey Data</span>
                                </div>
                                <p>You will first need to create an account using your email. Under "Office", select Survey Participant. After creating an account, you can download the survey results using the links at the top of the page.</p>
                                <p>*Note* Creating a user account requries receiving a confirmation code from data@fwc-data.com. Please reach out to us if you any have issues logging in.</p>
                                <p>For more information on the new portal and the features it provides, tune into our webinar on September 30th.</p>
                            </div></> :
                                <>{acceptedSubmission && earlyResultReleased ? (
                                    <>
                                        <div className='section'>
                                            <h4 className='section-heading'>Survey Access</h4>
                                            <p>Access the relevant survey files and related materials for download here</p>
                                            <a href="#self" ref={surveyMethodologyDownloadLinkRef} download id="download-survey-methodology" hidden={true}>hidden</a>
                                            <a href="#self" ref={earlySurveyBenchmarkResultDownloadLinkRef} download id="download-early-survey-benchmark" hidden={true}>hidden</a>
                                            <a href="#self" ref={earlySurveyPolicyResultDownloadLinkRef} download id="download-early-survey-policy" hidden={true}>hidden</a>
                                            <div className='actions-buttons-container' style={{ display: 'flex', flexDirection: 'row', marginTop: "1rem" }}>
                                                <div className='tooltip-container'>
                                                    <button className='btn btn-primary download' onClick={handleDownloadingMethodologyDoc}>Download Methodology and Overview</button>
                                                    <span className='tooltip-text'>Download Methodology and Overview</span>
                                                </div>
                                                <div className='tooltip-container'>
                                                    <button className='btn btn-primary download' onClick={handleDownloadingEarlySurveyBenchmarkResult}>Download Pay Benchmarking File</button>
                                                    <span className='tooltip-text'>Download Early Survey Pay Benchmarking Result - General Industry</span>
                                                </div>
                                                {earlyPolicyResultReleased && <div className='tooltip-container'>
                                                    <button className='btn btn-primary download' onClick={handleDownloadingEarlySurveyPolicyResult}>Download Policies and Design File</button>
                                                    <span className='tooltip-text'>Download Early Survey Policies and Design Result - General Industry</span>
                                                </div>}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className='section'>
                                            <div className='actions-buttons-container' style={{ display: 'flex', flexDirection: 'row' }}>
                                                <a href="#self" ref={questionnaireDonwnloadLinkRef} download id="download-questionnaire" hidden={true}>hidden</a>
                                                <button
                                                    className='btn btn-primary download'
                                                    onClick={handleDownloadingQuestionnaire}
                                                    disabled={isDownloading}
                                                    aria-busy={isDownloading}
                                                >
                                                    {isDownloading ? (
                                                        <>
                                                            Downloading...
                                                        </>
                                                    ) : (
                                                        <>
                                                            Download Questionnaire <i className="fa fa-download" aria-hidden="true"></i>
                                                        </>
                                                    )}
                                                </button>
                                                <button
                                                    className='btn btn-primary upload'
                                                    onClick={() => setShowUploadModal(true)}
                                                >Submit Questionnaire <i className="fa fa-upload" aria-hidden="true"></i>
                                                </button>
                                            </div>
                                            <p>For your convenience, you may download a {renderSampleLink()} with illustrative data to assist in completing your survey questionnaire</p>
                                        </div>
                                        <div className='section questionnaire-access'>
                                            <h4 className='section-heading'>Questionnaire Submission Portal</h4>
                                            <ul>
                                                <li>We encourage early submission, no later than the requested deadline (<b>May 15th</b> or <b>August 15th</b>, depending on your company's latest annual long-term incentive grant cycle)</li>
                                                <li>Upon submission, you'll receive a message indicating successful upload or any necessary corrections required before submission</li>
                                                <li>Our processing team will then review your submission and provide feedback or confirm acceptance, if needed</li>
                                            </ul>
                                        </div>
                                        <div className='section'>
                                            <h4 className='section-heading'>Survey Access</h4>
                                            <p>Once finalized, access the relevant survey files and related materials for download here</p>
                                        </div>
                                    </>
                                )}</>}
                            <div className='section'>
                                <h4 className='section-heading'>Contact Us</h4>
                                <p>For any queries, reach out to {(participantData?.accountExecutiveEmail) ?
                                    <span> your account representative <a href={`mailto:${participantData.accountExecutiveEmail}`} onClick={emailAccountExecutiveAnalyticsEvent} className='link'>{participantData.accountExecutiveName}</a> or</span> : null}
                                    <span> <a href='mailto:FWCSurvey@fwcook.com' onClick={emailFwcSurveyAnalyticsEvent} className='link'>FWCSurvey@fwcook.com</a></span></p>
                            </div>
                            <div className='section'>
                                <h4 className='section-heading'>Learn More</h4>
                                <p>To learn more about FW Cook and its capabilities and insights please visit our <span><a href="https://fwcook.com/" target="_blank" className='link' rel="noopener noreferrer">website</a></span>.</p>
                            </div>
                            <div className='thank-you-text'>
                                Thank you for your contribution to shaping the future of executive compensation practices with the FW Cook Executive Compensation Survey
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
    };

    const renderSampleLink = () => {
        if (!sampleSurveyFileLink?.url) {
            return <span>sample questionnaire (link unavailable)</span>;
        }
        return (
            <a
                href={sampleSurveyFileLink.url}
                onClick={sampleSurveyDownloadedEvent}
                className='link'
                target='_blank'
                rel="noreferrer"
            >
                sample questionnaire
            </a>
        );
    };

    return (
        <>
            <NavBar landingPagePath={`/ecs-survey/${participantId}`} />
            {renderContent()}
        </>
    )
}